import { FunctionComponent, useState } from "react";
import { useLocation, useNavigate } from "react-router";

// images
import imageTimberLogo from "images/timber-logo-120-60.png";
import defaultAvatar from "images/default-avatar.png";

// action creators
import { logUserOut } from "modules/user/actions";
import {
  StyledLogo,
  StyledCollapse,
  StyledTopNavigation,
  StyledToggle,
  StyledLink,
  StyledContainer,
  StyledBars,
} from "./top-navigation.styles";
import RequireRole from "./require-role";
import { useCollectorState } from "modules/collector/hook";
import { useAppState } from "modules/app/hook";
import Image from "./image";
import { faBars } from "@fortawesome/free-solid-svg-icons";
import Dropdown, { DropdownItem } from "./dropdown";
import { useUserState } from "modules/user/hook";

interface TopNavigationProps {
  full?: boolean;
}

const TopNavigation: FunctionComponent<TopNavigationProps> = ({
  full = false,
}) => {
  const [mobileIsOpen, toggleMobileIsOpen] = useState(false);
  const collector = useCollectorState();
  const appState = useAppState();
  const navigate = useNavigate();
  const { selectedOrganizationUnitData } = appState;
  const { pathname } = useLocation();
  const user = useUserState();
  const { data } = user;

  const isSysadmin = data?.systemRole === "sysadmin";

  const logout = () => {
    logUserOut(navigate);
  };

  // Utility function for navigation and closing mobile menu
  const handleNav = (to: string) => {
    navigate(to);
    toggleMobileIsOpen(false);
  };

  const renderCollectorText = () => {
    const { images } = collector;

    if (images.length === 0) {
      return (
        <StyledLink
          onClick={() => handleNav("/share")}
          $match={pathname === "/share"}
        >
          Share images
        </StyledLink>
      );
    }

    return (
      <StyledLink
        color="orange"
        onClick={() => handleNav("/share")}
        $match={pathname === "/share"}
      >
        {`Share ${images.length} images`}
      </StyledLink>
    );
  };

  const dropdownItems: DropdownItem[] = [
    { text: "My profile", onClick: () => navigate("/profile") },
  ];
  if (isSysadmin)
    dropdownItems.push({
      text: "Settings",
      onClick: () => navigate("/settings"),
    });
  dropdownItems.push({ text: "Logout", onClick: () => logout() });

  return (
    <StyledTopNavigation>
      <StyledContainer>
        <StyledLogo onClick={() => handleNav("/")}>
          {selectedOrganizationUnitData && selectedOrganizationUnitData.logo ? (
            <Image publicId={selectedOrganizationUnitData.logo} />
          ) : (
            <img src={imageTimberLogo} height="100%" alt="" />
          )}
        </StyledLogo>

        {full && (
          <StyledToggle onClick={() => toggleMobileIsOpen(!mobileIsOpen)}>
            <StyledBars icon={faBars} size="lg" />
          </StyledToggle>
        )}

        {full && (
          <StyledCollapse $isOpen={mobileIsOpen}>
            <StyledLink
              onClick={() => handleNav("/")}
              $match={pathname === "/"}
            >
              Products
            </StyledLink>

            <RequireRole requiredRole="user">
              <StyledLink
                onClick={() => handleNav("/new")}
                $match={pathname === "/new"}
              >
                New Product
              </StyledLink>
            </RequireRole>

            <RequireRole requiredRole="admin">
              <StyledLink
                onClick={() => handleNav("/attributes")}
                $match={pathname === "/attributes"}
              >
                Attributes
              </StyledLink>
            </RequireRole>

            <RequireRole requiredRole="admin">
              <StyledLink
                onClick={() => handleNav("/units")}
                $match={pathname === "/units"}
              >
                Organization Units
              </StyledLink>
            </RequireRole>

            <RequireRole requiredRole="admin">
              <StyledLink
                onClick={() => handleNav("/users")}
                $match={pathname === "/users"}
              >
                Users
              </StyledLink>
            </RequireRole>

            <RequireRole requiredRole="admin">
              <StyledLink
                onClick={() => handleNav("/groups")}
                $match={pathname === "/groups"}
              >
                Groups
              </StyledLink>
            </RequireRole>

            <RequireRole requiredRole="user">
              {renderCollectorText()}
            </RequireRole>

            <Dropdown
              label={
                <img
                  src={defaultAvatar}
                  alt=""
                  width="24"
                  height="24"
                  style={{ marginRight: 0 }}
                />
              }
              items={dropdownItems}
            />
          </StyledCollapse>
        )}
      </StyledContainer>
    </StyledTopNavigation>
  );
};

export default TopNavigation;
